// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import { PlusIcon } from "src/assets/icons";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useClinicPackages } from "src/services/clinics/hooks/clinic-packages/useClinicPackages";
import { useAddPresetExams } from "src/hooks/exams/useAddPresetExams";

// Styles
import {
  ModalCustomCancel,
  ExamSliderButton,
} from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

// Utils
import { getTooltipText } from "src/utils/exams/examReportsHelper";
import { getCapitalizedText } from "src/utils/text/textHelper";

// Types
import { ClinicPackageType } from "src/services/clinics/types";
import { ExamType } from "src/services/exams/types";

type PresetExamButtonsType = {
  patientId: string;
  isLoadingExams: boolean;
  refetchExams: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<ExamType[], Error>>;
};

const PresetExamButtons = ({
  patientId,
  isLoadingExams,
  refetchExams,
}: PresetExamButtonsType) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { addPresetExam } = useAddPresetExams();

  const [disableButtons, setDisableButtons] = useState(false);

  const {
    data: packageData,
    isLoading: isLoadingPackages,
    isFetching: isFetchingPackages,
    isRefetching: isRefetchingPackages,
  } = useClinicPackages(clinicInfo?.id);

  const isLoading = useMemo(
    () =>
      isLoadingPackages ||
      isFetchingPackages ||
      isRefetchingPackages ||
      isLoadingExams,
    [
      isLoadingPackages,
      isFetchingPackages,
      isRefetchingPackages,
      isLoadingExams,
    ]
  );

  const sortedPackageData = useMemo(
    () => (packageData ?? [])?.sort((a, b) => a?.name?.localeCompare(b?.name)),
    [packageData]
  );

  // handle adding a new exam, refetching data, and displaying a toast
  const onConfirmAddExam = useCallback(
    async (presetExam: ClinicPackageType) => {
      setDisableButtons(true);

      try {
        await addPresetExam({
          patientId,
          presetExam,
        });
        await refetchExams();
      } catch (error) {
        // do nothing
      } finally {
        setDisableButtons(false);
      }
    },
    [addPresetExam, refetchExams, patientId]
  );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        gap: "8px",
        marginBottom: isLoading || sortedPackageData.length > 0 ? "8px" : "0px",
      }}
    >
      {/* Loading skeletons */}
      {isLoading &&
        Array.from({
          length: 3,
        })?.map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            height={UI_SIZES.inputHeight}
            sx={{
              minWidth: 0,
              width: { xs: "33.3%", sm: "150px" },
              borderRadius: "4px",
            }}
          />
        ))}

      {/* Package buttons */}
      {!isLoading &&
        sortedPackageData.length > 0 &&
        sortedPackageData?.map((clinicPackage) => (
          <Tooltip
            key={clinicPackage?.id}
            title={
              <div style={{ whiteSpace: "pre-line" }}>
                {`${getCapitalizedText(clinicPackage?.name)}:\n ${getTooltipText(JSON.parse(clinicPackage?.content), t)}`}
              </div>
            }
          >
            <Button
              disabled={disableButtons}
              startIcon={<PlusIcon />}
              sx={{
                ...ModalCustomCancel(theme),
                ...(theme.palette.mode === "dark" &&
                  ExamSliderButton(theme, false)),
                minWidth: 0,
                maxWidth: { xs: "33.3%", sm: "200px" },
                overflow: "hidden",
                padding: "8px",
              }}
              onClick={() => onConfirmAddExam(clinicPackage)}
            >
              <Box
                sx={{
                  display: "flex",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                <Typography fontSize="14px" noWrap>
                  {clinicPackage?.name}
                </Typography>
              </Box>
            </Button>
          </Tooltip>
        ))}
    </Box>
  );
};

export default PresetExamButtons;
